/**
 * Secao "Cartoes de Saude" da tela de beneficiario (coluna direita, entre
 * Panorama e Atendimentos).
 *
 * REGRA DE OURO: a operadora e cara. A secao nasce COLAPSADA e NAO faz fetch.
 * O fetch acontece uma unica vez, na primeira expansao (lazy). Colapsar e
 * expandir de novo reaproveita o estado, sem refazer a chamada. O refresh
 * manual (botao) e a unica forma de forcar ida a operadora.
 *
 * O payload da app-api (my_cards) traz 3 grupos: carteirinha, unimed,
 * reciprocidade — RENDERIZADOS a partir dos dados estruturados (o numero que o
 * atendente copia varia por tipo).
 *
 * QUEM MANDA E O ENDPOINT DE DADOS: a lista de cartoes do beneficiario sai
 * dele (carteirinha/unimed/reciprocidade). O endpoint de IMAGENS as vezes
 * devolve arte pra tipos que o beneficiario NAO tem (ex.: luminar/uniodonto) —
 * essas artes sao ignoradas, senao a tela inventa cartoes (bug: mostrava 8
 * onde a operadora reporta 6).
 *
 * A operadora tambem serve a IMAGEM REAL de cada cartao (elosaude, funcional,
 * unimed, uniodonto e os parceiros de reciprocidade). Quando existe imagem, ELA
 * e' a miniatura no grid — o cartao desenhado so aparece como fallback pra quem
 * nao tem arte (senao a tela ficava duplicada). Clicar abre o modal com a arte
 * ampliada + os campos + copiar numero.
 */
import React, { useState, useEffect } from 'react';
import { CreditCard, ChevronDown, ChevronUp, RefreshCw, Clock, AlertTriangle, Copy, Check, X,
         Maximize2, Download, Image as ImageIcon } from 'lucide-react';
import { beneficiariosAPI } from '../services/api';

const TEAL = 'var(--brand, #20a490)';
const TEAL_ESCURO = '#0f5e56';

// Mapa por tipo de cartao: rotulo, se e o principal (fundo teal), qual campo e
// o "numero", o plano e a lista de campos exibidos no detalhe. Fallbacks
// encadeados porque nem todo cartao traz todos os campos.
const val = (c, ...chaves) => {
  for (const k of chaves) {
    const v = c[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
  }
  return null;
};

const MAPA = {
  carteirinha: {
    rotulo: 'Carteirinha', principal: true,
    numero: (c) => val(c, 'MATRICULA'),
    plano: (c) => val(c, 'DS_PLANO_CARTEIRA', 'PRIMARIO', 'DS_PLANO'),
    campos: [
      ['Beneficiário', (c) => val(c, 'NM_SOCIAL', 'NOME_DO_BENEFICIARIO')],
      ['Plano', (c) => val(c, 'DS_PLANO_CARTEIRA', 'PRIMARIO')],
      ['Segmentação', (c) => val(c, 'SEGMENTACAO')],
      ['Contratação', (c) => val(c, 'CONTRATACAO')],
      ['Empresa', (c) => val(c, 'EMPRESA')],
      ['Titular', (c) => val(c, 'NOME_TITULAR')],
      ['CNS', (c) => val(c, 'NR_CNS')],
      ['Validade', (c) => val(c, 'VALIDADE')],
    ],
  },
  unimed: {
    rotulo: 'Unimed', principal: false,
    numero: (c) => val(c, 'MATRICULA_UNIMED'),
    plano: (c) => val(c, 'PLANO'),
    campos: [
      ['Beneficiário', (c) => val(c, 'NM_SOCIAL', 'NOME')],
      ['Plano', (c) => val(c, 'PLANO')],
      ['Abrangência', (c) => val(c, 'ABRANGENCIA')],
      ['Acomodação', (c) => val(c, 'ACOMODACAO')],
      ['Rede', (c) => val(c, 'REDE_ATENDIMENTO')],
      ['Titular', (c) => val(c, 'NOME_TITULAR')],
      ['CNS', (c) => val(c, 'NR_CNS')],
      ['Validade', (c) => val(c, 'Validade', 'VIGENCIA')],
    ],
  },
  reciprocidade: {
    rotulo: 'Reciprocidade', principal: false,
    numero: (c) => val(c, 'CD_MATRICULA_RECIPROCIDADE', 'MATRICULA'),
    plano: (c) => val(c, 'PLANO_ELOSAUDE'),
    campos: [
      ['Beneficiário', (c) => val(c, 'NM_SOCIAL', 'NOME_BENEFICIARIO')],
      ['Prestador', (c) => val(c, 'PRESTADOR_RECIPROCIDADE')],
      ['Plano de origem', (c) => val(c, 'PLANO_ELOSAUDE')],
      ['Adesão', (c) => val(c, 'DT_ADESAO')],
      ['CNS', (c) => val(c, 'NR_CNS')],
      ['Validade', (c) => val(c, 'DT_VALIDADE_CARTEIRA')],
    ],
  },
};

const S = {
  card: { background: '#fff', borderRadius: '12px', border: '1px solid #e8e8e8', padding: '20px', marginBottom: '16px' },
  title: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '14px', marginTop: '14px', alignItems: 'start' },
  // Miniatura da arte real: cartoes vem VERTICAIS (proporcao ~0.42-0.77) e com
  // alturas diferentes. Fixamos a ALTURA e deixamos a largura seguir, assim as
  // minicarteirinhas alinham em linha e nenhuma e' recortada (contain).
  // Miniatura da arte real: cartoes verticais, alturas diferentes. Altura fixa
  // + contain: alinham pela base sem moldura e sem recorte.
  miniImg: { maxHeight: '190px', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: '8px', display: 'block', boxShadow: '0 1px 6px rgba(0,0,0,0.13)' },
  miniBox: { height: '190px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cartaoBase: { aspectRatio: '1.586', borderRadius: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '14px', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.12s, box-shadow 0.12s' },
  rodape: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '14px', fontSize: '0.72rem', color: '#aaa' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', display: 'flex', alignItems: 'center', padding: '4px' },
};

function achatarCartoes(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const out = [];
  for (const chave of ['carteirinha', 'unimed', 'reciprocidade']) {
    for (const c of (payload[chave] || [])) out.push({ _chave: chave, dados: c });
  }
  return out;
}

/**
 * Casa cada cartao (dados) com a imagem real da operadora.
 * - unimed      -> tipo "unimed"
 * - carteirinha -> tipo "elosaude" (a arte do cartao do beneficiario)
 * - reciprocidade -> tipo = PRESTADOR_RECIPROCIDADE (ex.: "Funcional" -> funcional)
 */
// Alguns parceiros de reciprocidade tem a arte servida sob um tipo DIFERENTE do
// nome do prestador. Ex.: a reciprocidade "EVIDA" tem a imagem sob o tipo
// 'luminar' (type=evida da 404; type=luminar da 200). Mapa nome->tipo de imagem.
const ALIAS_PRESTADOR_IMAGEM = { evida: 'luminar' };

/**
 * Carteirinha EXCLUSIVAMENTE odontologica (ex.: "B - Odonto Basico", segmentacao
 * "ODONT"). A arte certa dela e o cartao da UNIODONTO — nao a do cartao medico.
 * Sem isso, o beneficiario com plano medico + odonto via DUAS miniaturas com a
 * MESMA imagem (o cartao medico repetido), que e a duplicata que a operacao
 * reclamou, e o cartao odontologico de verdade nunca aparecia.
 */
function ehCarteirinhaOdonto(cartao) {
  if (cartao._chave !== 'carteirinha') return false;
  const seg = (val(cartao.dados, 'SEGMENTACAO') || '').toUpperCase();
  if (seg.includes('ODONT') && !seg.includes('AMB') && !seg.includes('HOSP')) return true;
  const plano = (val(cartao.dados, 'DS_PLANO_CARTEIRA', 'DS_PLANO', 'PRIMARIO') || '').toUpperCase();
  return plano.includes('ODONTO') && !/AMB|HOSP|ENFERMARIA|APARTAMENTO/.test(plano);
}

function imagemDoCartao(cartao, porTipo) {
  if (!porTipo) return null;
  if (cartao._chave === 'imagem') return cartao._somenteImagem || null;
  if (cartao._chave === 'unimed') return porTipo.unimed || null;
  if (cartao._chave === 'carteirinha') {
    // Odontologico -> arte da Uniodonto (cai pra elosaude se a operadora nao servir)
    if (ehCarteirinhaOdonto(cartao)) return porTipo.uniodonto || porTipo.elosaude || null;
    return porTipo.elosaude || porTipo.funcional || null;
  }
  if (cartao._chave === 'reciprocidade') {
    const p = (val(cartao.dados, 'PRESTADOR_RECIPROCIDADE') || '').trim().toLowerCase();
    return porTipo[ALIAS_PRESTADOR_IMAGEM[p] || p] || null;
  }
  return null;
}

function textoAtualizado(atualizadoEm) {
  if (!atualizadoEm) return '';
  const diffMin = Math.max(0, Math.round((Date.now() - new Date(atualizadoEm).getTime()) / 60000));
  if (diffMin < 60) return `Atualizado há ${diffMin}min`;
  const h = Math.round(diffMin / 60);
  if (h < 48) return `Atualizado há ${h}h`;
  return `Atualizado há ${Math.round(h / 24)}d`;
}

const ativo = (c) => val(c, 'SN_ATIVO') !== 'N';

export default function CartoesSaude({ cpf }) {
  const [aberto, setAberto] = useState(false);
  const [carregado, setCarregado] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [cartoes, setCartoes] = useState([]);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [erro, setErro] = useState(false);
  const [detalhe, setDetalhe] = useState(null); // cartao aberto no modal
  // Imagens dos cartoes servidas pela operadora: [{tipo, rotulo, url}].
  // Cada beneficiario pode ter funcional, unimed, os dois ou nenhum.
  const [imagens, setImagens] = useState([]);

  // Libera os object URLs dos blobs ao trocar de beneficiario / desmontar.
  useEffect(() => () => { imagens.forEach((i) => URL.revokeObjectURL(i.url)); }, [imagens]);

  async function buscarImagens() {
    try {
      const { data } = await beneficiariosAPI.getCartaoImagensDisponiveis(cpf);
      const disponiveis = data?.imagens || [];
      if (!disponiveis.length) return;
      const baixadas = await Promise.all(disponiveis.map(async ({ tipo, rotulo }) => {
        const resp = await beneficiariosAPI.getCartaoImagem(cpf, tipo);
        if (resp.status === 204 || !resp.data || resp.data.size === 0) return null;
        return { tipo, rotulo, url: URL.createObjectURL(resp.data) };
      }));
      setImagens(baixadas.filter(Boolean));
    } catch (_) {
      // imagem e' um plus: falhando, os cartoes desenhados seguem valendo
    }
  }

  async function buscar({ refresh = false } = {}) {
    if (refresh) setAtualizando(true); else setCarregando(true);
    setErro(false);
    try {
      const { data } = await beneficiariosAPI.getCartoes(cpf, refresh);
      setCartoes(achatarCartoes(data.cartoes));
      setCacheInfo(data.cache || null);
      setCarregado(true);
      buscarImagens(); // paralelo, nao bloqueia os dados
    } catch (e) {
      if (!cartoes.length) setErro(true); // mantem os antigos se um refresh falhar
    } finally {
      setCarregando(false);
      setAtualizando(false);
    }
  }

  function alternar() {
    const vai = !aberto;
    setAberto(vai);
    if (vai && !carregado && !carregando) buscar();
  }

  // Indice tipo -> imagem, e quais tipos ja foram usados por um cartao de dados
  const porTipo = React.useMemo(
    () => Object.fromEntries(imagens.map((i) => [i.tipo, i])), [imagens]);

  // Toda arte que a operadora serve e que NENHUM cartao de dados consumiu vira
  // um cartao proprio. E assim que o cartao da UNIODONTO aparece pra quem tem o
  // odonto embutido no plano medico (nao ha registro odonto separado no payload,
  // mas a imagem existe) — antes ela era baixada e descartada.
  const cartoesExibidos = React.useMemo(() => {
    const usados = new Set(
      cartoes.map((c) => imagemDoCartao(c, porTipo)?.tipo).filter(Boolean)
    );
    const sobrando = imagens.filter((i) => !usados.has(i.tipo));
    return [...cartoes, ...sobrando.map((i) => ({ _chave: 'imagem', _somenteImagem: i, dados: {} }))];
  }, [cartoes, imagens, porTipo]);
  const degradado = cacheInfo?.origem === 'cache_degradado';

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={alternar}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, flex: 1 }}>
          <div style={S.title}><CreditCard size={14} /> Cartões de Saúde</div>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {aberto && carregado && (
            <button title="Atualizar cartões" onClick={() => buscar({ refresh: true })}
                    disabled={atualizando} style={S.iconBtn}>
              <RefreshCw size={15} style={atualizando ? { animation: 'girar 0.9s linear infinite' } : undefined} />
            </button>
          )}
          <button onClick={alternar} style={S.iconBtn}>
            {aberto ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
        </div>
      </div>

      <style>{`@keyframes girar { to { transform: rotate(360deg); } }`}</style>

      {aberto && (
        <div>
          {carregando && <Skeleton />}

          {!carregando && erro && (
            <div style={{ marginTop: '14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', color: '#b91c1c', fontSize: '0.82rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <AlertTriangle size={15} /> Não foi possível carregar os cartões agora.
              </div>
              <button onClick={() => buscar()} style={{ background: '#fff', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '0.78rem' }}>
                Tentar novamente
              </button>
            </div>
          )}

          {!carregando && !erro && carregado && (
            <>
              {cartoesExibidos.length === 0 ? (
                <div style={{ marginTop: '14px', color: '#aaa', fontSize: '0.82rem' }}>
                  Nenhum cartão encontrado para este beneficiário.
                </div>
              ) : (
                <div style={S.grid}>
                  {cartoesExibidos.map((c, i) => (
                    <CartaoCelula key={i} cartao={c} imagem={imagemDoCartao(c, porTipo)}
                                  onAbrir={() => setDetalhe({ ...c, _imagem: imagemDoCartao(c, porTipo) })} />
                  ))}
                </div>
              )}

              {cacheInfo?.atualizado_em && (
                <div style={S.rodape}>
                  <Clock size={12} />
                  {textoAtualizado(cacheInfo.atualizado_em)}
                  {' · '}{cacheInfo.origem === 'operadora' ? 'operadora' : 'cache'}
                  {degradado && ' · operadora indisponível, exibindo última versão'}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {detalhe && <CartaoModal cartao={detalhe} onFechar={() => setDetalhe(null)} />}
    </div>
  );
}

function Skeleton() {
  return (
    <div style={S.grid}>
      {[0, 1].map((i) => (
        <div key={i} style={{ aspectRatio: '1.586', borderRadius: '10px', background: '#eee', animation: 'pulsar 1.2s ease-in-out infinite' }} />
      ))}
      <style>{`@keyframes pulsar { 0%,100% { opacity: 1 } 50% { opacity: 0.5 } }`}</style>
    </div>
  );
}

function CartaoCelula({ cartao, imagem, onAbrir }) {
  const cfg = MAPA[cartao._chave];
  const d = cartao.dados;
  const principal = cfg?.principal;
  const numero = cfg?.numero(d);
  const plano = cfg?.plano(d);
  // Com imagem real da operadora, ELA e a miniatura (nada de cartao desenhado
  // por cima — era isso que duplicava a tela).
  if (imagem) {
    return (
      <div onClick={onAbrir} style={{ cursor: 'pointer' }}>
        <div style={{ ...S.miniBox, position: 'relative' }}>
          <img src={imagem.url} alt={cfg?.rotulo || imagem.rotulo} style={S.miniImg} />
          {!ativo(d) && (
            <span style={{ position: 'absolute', top: 4, right: 4, fontSize: '0.6rem', fontWeight: 700,
                           background: '#e74c3c', color: '#fff', padding: '1px 6px', borderRadius: '6px' }}>INATIVO</span>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '0.7rem' }}>
          {/* Rotulo = NOME do cartao (imagem.rotulo, servido pela operadora por tipo:
              "Cartao Unimed", "Cartao funcional"...). O nome do PLANO vira subtitulo
              discreto — antes o plano vinha como rotulo e confundia (ex.: Funcional
              aparecia como "PLANO ELOSAU"). */}
          <span style={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {imagem.rotulo || cfg?.rotulo}
          </span>
          <span style={{ color: TEAL, fontWeight: 600, flexShrink: 0 }}>Ver ›</span>
        </div>
        {plano && (
          <div style={{ color: '#999', fontSize: '0.62rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {plano}
          </div>
        )}
      </div>
    );
  }
  const estilo = {
    ...S.cartaoBase,
    background: principal ? TEAL_ESCURO : '#f4f6f7',
    color: principal ? '#fff' : '#333',
    border: principal ? 'none' : '1px solid #e8e8e8',
  };
  return (
    <div onClick={onAbrir}
         onMouseEnter={(e) => { e.currentTarget.firstChild.style.transform = 'translateY(-2px)'; e.currentTarget.firstChild.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
         onMouseLeave={(e) => { e.currentTarget.firstChild.style.transform = 'none'; e.currentTarget.firstChild.style.boxShadow = 'none'; }}>
      <div style={estilo}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <CreditCard size={20} color={principal ? 'rgba(255,255,255,0.85)' : TEAL} />
          {!ativo(d) && <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#e74c3c', color: '#fff', padding: '1px 6px', borderRadius: '6px' }}>INATIVO</span>}
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cfg?.rotulo}</div>
          <div style={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '0.02em', fontFamily: 'monospace' }}>{numero || '—'}</div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '0.7rem' }}>
        <span style={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{plano || cfg?.rotulo}</span>
        <span style={{ color: TEAL, fontWeight: 600, flexShrink: 0 }}>Ver ›</span>
      </div>
    </div>
  );
}

/** Visualizador em tela cheia: a arte do cartao como protagonista. */
function Lightbox({ url, rotulo, onFechar }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onFechar]);

  return (
    <div onClick={onFechar}
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '24px' }}>
      <button onClick={onFechar} title="Fechar (Esc)"
              style={{ position: 'absolute', top: '18px', right: '20px', background: 'rgba(255,255,255,0.12)',
                       border: 'none', borderRadius: '50%', width: '38px', height: '38px', cursor: 'pointer',
                       color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <X size={20} />
      </button>
      <img src={url} alt={rotulo || 'cartão'} onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: '96vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: '8px',
                    boxShadow: '0 10px 50px rgba(0,0,0,0.5)' }} />
    </div>
  );
}

/** Copia a arte do cartao pra area de transferencia (PNG). Baixa se o
 *  navegador nao permitir escrever imagem no clipboard. */
async function copiarImagemCartao(url, nomeArquivo) {
  const baixar = () => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${nomeArquivo || 'carteirinha'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  try {
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') { baixar(); return 'baixado'; }
    const resp = await fetch(url);          // blob: same-origin, sem CORS
    const blob = await resp.blob();
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    return 'copiado';
  } catch (_) {
    baixar();
    return 'baixado';
  }
}

/** Barra de acoes discreta da arte do cartao. */
function AcoesImagem({ url, rotulo, onAmpliar, extra }) {
  const [estado, setEstado] = useState(null); // 'copiado' | 'baixado'
  const btn = (ativo) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px',
    borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
    border: `1px solid ${ativo ? '#27ae60' : '#e0e0e0'}`,
    background: ativo ? '#27ae6012' : '#fff', color: ativo ? '#27ae60' : TEAL,
  });
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
      <button onClick={onAmpliar} style={btn(false)} title="Ver em tela cheia">
        <Maximize2 size={14} /> Ampliar
      </button>
      <button style={btn(estado === 'copiado')} title="Copiar a imagem do cartão"
              onClick={async () => {
                const r = await copiarImagemCartao(url, rotulo);
                setEstado(r);
                setTimeout(() => setEstado(null), 1800);
              }}>
        {estado === 'copiado' ? <><Check size={14} /> Copiado</>
          : estado === 'baixado' ? <><Download size={14} /> Baixado</>
          : <><ImageIcon size={14} /> Copiar imagem</>}
      </button>
      {extra}
    </div>
  );
}

function CartaoModal({ cartao, onFechar }) {
  const [copiado, setCopiado] = useState(false);
  const [zoom, setZoom] = useState(false);
  // Cartao que so tem imagem (sem registro de dados): modal simples com a arte.
  if (cartao._somenteImagem) {
    const im = cartao._somenteImagem;
    return (
      <>
        {zoom && <Lightbox url={im.url} rotulo={im.rotulo} onFechar={() => setZoom(false)} />}
        <div onClick={onFechar}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', overflow: 'hidden', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <strong style={{ fontSize: '0.9rem' }}>{im.rotulo}</strong>
              <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa' }}><X size={18} /></button>
            </div>
            <img src={im.url} alt={im.rotulo} onClick={() => setZoom(true)} title="Clique para ampliar"
                 style={{ width: '100%', maxHeight: '72vh', objectFit: 'contain', borderRadius: '10px', display: 'block', cursor: 'zoom-in' }} />
            <div style={{ marginTop: '12px' }}>
              <AcoesImagem url={im.url} rotulo={im.rotulo} onAmpliar={() => setZoom(true)} />
            </div>
          </div>
        </div>
      </>
    );
  }
  const cfg = MAPA[cartao._chave];
  const d = cartao.dados;
  const principal = cfg?.principal;
  const numero = cfg?.numero(d);

  function copiar() {
    const limpo = (numero || '').replace(/\s/g, '');
    if (!limpo) return;
    navigator.clipboard.writeText(limpo).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    }).catch(() => {});
  }

  const campos = (cfg?.campos || []).map(([label, fn]) => [label, fn(d)]).filter(([, v]) => v);

  return (
    <>
      {zoom && cartao._imagem && (
        <Lightbox url={cartao._imagem.url} rotulo={cfg?.rotulo} onFechar={() => setZoom(false)} />
      )}
      <div onClick={onFechar}
           style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
        <div onClick={(e) => e.stopPropagation()}
             style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
        {cartao._imagem && (
          <img src={cartao._imagem.url} alt={cfg?.rotulo || 'cartão'}
               onClick={() => setZoom(true)} title="Clique para ampliar"
               style={{ width: '100%', maxHeight: '64vh', objectFit: 'contain', display: 'block', background: '#f4f6f7', cursor: 'zoom-in' }} />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 700, color: '#2c3e50' }}>{cfg?.rotulo}</div>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa' }}><X size={18} /></button>
        </div>

        <div style={{ padding: '18px' }}>
          {/* Cartao renderizado por nos: SO como fallback quando NAO veio a
              imagem oficial da operadora. Se ja temos a imagem da API, nao
              duplica com um cartao inventado. */}
          {!cartao._imagem && (
            <div style={{ aspectRatio: '1.586', borderRadius: '12px', padding: '18px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                          background: principal ? TEAL_ESCURO : '#f4f6f7', color: principal ? '#fff' : '#333', border: principal ? 'none' : '1px solid #e8e8e8' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <CreditCard size={26} color={principal ? 'rgba(255,255,255,0.85)' : TEAL} />
                {!ativo(d) && <span style={{ fontSize: '0.65rem', fontWeight: 700, background: '#e74c3c', color: '#fff', padding: '2px 8px', borderRadius: '6px' }}>INATIVO</span>}
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cfg?.rotulo}</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.03em' }}>{numero || '—'}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.85, marginTop: '2px' }}>{val(d, 'NM_SOCIAL', 'NOME_DO_BENEFICIARIO', 'NOME', 'NOME_BENEFICIARIO')}</div>
              </div>
            </div>
          )}

          {/* Acoes discretas: ampliar / copiar imagem / copiar numero */}
          {(() => {
            const btnNumero = numero ? (
              <button onClick={copiar} title="Copiar o número da carteirinha"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
                               padding: '5px 12px', borderRadius: '8px', cursor: 'pointer',
                               fontSize: '0.8rem', fontWeight: 600,
                               border: `1px solid ${copiado ? '#27ae60' : '#e0e0e0'}`,
                               background: copiado ? '#27ae6012' : '#fff',
                               color: copiado ? '#27ae60' : TEAL }}>
                {copiado ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar nº</>}
              </button>
            ) : null;
            if (!cartao._imagem) {
              return btnNumero && <div style={{ marginTop: '14px' }}>{btnNumero}</div>;
            }
            return (
              <div style={{ marginTop: '2px' }}>
                <AcoesImagem url={cartao._imagem.url} rotulo={cfg?.rotulo}
                             onAmpliar={() => setZoom(true)} extra={btnNumero} />
              </div>
            );
          })()}

          {/* Demais campos */}
          {campos.length > 0 && (
            <div style={{ marginTop: '16px', display: 'grid', gap: '10px' }}>
              {campos.map(([label, v]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '0.82rem', borderBottom: '1px solid #f4f4f4', paddingBottom: '8px' }}>
                  <span style={{ color: '#999' }}>{label}</span>
                  <span style={{ color: '#333', fontWeight: 500, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );
}
